import { createSlice } from '@reduxjs/toolkit';

import { queryKeys, urlUtils } from '../../../utils/url';

import { initSucceeded } from '../../actions/cruiseListActions';

// The declared defaults for each filter group. The two clear actions reset back to this, copying
// each array rather than sharing it.
//
// Note `duration` has no `dayLabel` key: `initSucceeded` adds one. That was true before the
// conversion too, so it is left as it was rather than declared here, which would change the
// pre-init shape from `undefined` to `''`.
const filterGroups = {
  departureMonth: {
    order: 0,
    id: 'departureMonth',
    heading: '',
    options: [],
    selected: [],
  },
  shipRating: {
    order: 1,
    id: 'shipRating',
    heading: '',
    options: [],
    selected: [],
  },
  duration: {
    order: 2,
    id: 'duration',
    heading: '',
    toLabel: '',
    daysLabel: '',
    minValue: null,
    maxValue: null,
    selectedMinValue: null,
    selectedMaxValue: null,
  },
  cruiseCategory: {
    order: 3,
    id: 'cruiseCategory',
    heading: '',
    options: [],
    selected: [],
  },
  destination: {
    order: 4,
    id: 'destination',
    heading: '',
    numberOfDestinationsHeading: '',
    toLabel: '',
    destinationsLabel: '',
    options: [],
    minNumberOfDestinations: null,
    maxNumberOfDestinations: null,
    selected: [],
    selectedMinNumberOfDestinations: null,
    selectedMaxNumberOfDestinations: null,
  },
  shippingCompany: {
    order: 5,
    id: 'shippingCompany',
    heading: '',
    options: [],
    selected: [],
  },
};

// Built with fresh arrays so state never shares structure with the const above.
const freshGroups = () =>
  Object.fromEntries(
    Object.entries(filterGroups).map(([key, group]) => [
      key,
      { ...group, ...(group.options ? { options: [] } : {}), ...(group.selected ? { selected: [] } : {}) },
    ]),
  );

const initialState = {
  filterChangesCounter: 0,
  filterPanelIsVisible: false,
  filterGroups: freshGroups(),
};

// Toggling a value in a `selected` list. Plain `splice` mutates and returns the **removed**
// items, which is why its return value is unused: the mutation on the immer draft is the point.
const toggleSelected = (selected, value) => {
  const index = selected?.indexOf(value) ?? -1;
  const checked = !!selected && selected.length > 0 && index !== -1;

  if (checked) {
    selected.splice(index, 1);
  } else {
    // Throws when `selected` is undefined, exactly as `copySelected.push` did before.
    selected.push(value);
  }
};

const parseList = (value) => (value ? value.split(',') : null);

// All ten actions here are slice-generated: no thunk dispatches them. The query listener in
// `middleware/cruiseListQuery.js` matches nine of them and mirrors the result into the URL.
//
// `filterChangesCounter` is written by nine of them and **read by nothing** in this view — no
// selector and no component. Kept rather than removed, the same call Task 175723 recorded for
// hotel-list's `hasChangedSortOrder`. flight-list's counter of the same name *is* read.
const filterSlice = createSlice({
  name: 'filter',
  initialState,
  reducers: {
    filterPanelToggled: (state, action) => {
      state.filterPanelIsVisible = action.payload;
    },
    filtersCleared: (state) => {
      const groups = state.filterGroups;
      groups.departureMonth.selected = [...filterGroups.departureMonth.selected];
      groups.shipRating.selected = [...filterGroups.shipRating.selected];
      groups.duration.selectedMinValue = filterGroups.duration.selectedMinValue;
      groups.duration.selectedMaxValue = filterGroups.duration.selectedMaxValue;
      groups.cruiseCategory.selected = [...filterGroups.cruiseCategory.selected];
      groups.destination.selected = [...filterGroups.destination.selected];
      groups.destination.selectedMinNumberOfDestinations = filterGroups.destination.selectedMinNumberOfDestinations;
      groups.destination.selectedMaxNumberOfDestinations = filterGroups.destination.selectedMaxNumberOfDestinations;
      groups.shippingCompany.selected = [...filterGroups.shippingCompany.selected];

      state.filterChangesCounter += 1;
    },
    // Only five of the six groups are listed, as before: `duration` has no case, so clearing it
    // bumps the counter and changes nothing. The counter bump sits outside the switch, so an
    // unrecognised group key also still bumps it.
    singleFilterCleared: (state, action) => {
      const groups = state.filterGroups;
      switch (action.payload) {
        case 'departureMonth':
          groups.departureMonth.selected = [...filterGroups.departureMonth.selected];
          break;
        case 'shipRating':
          groups.shipRating.selected = [...filterGroups.shipRating.selected];
          break;
        case 'cruiseCategory':
          groups.cruiseCategory.selected = [...filterGroups.cruiseCategory.selected];
          break;
        case 'destination':
          groups.destination.selected = [...filterGroups.destination.selected];
          break;
        case 'shippingCompany':
          groups.shippingCompany.selected = [...filterGroups.shippingCompany.selected];
          break;
        default:
          break;
      }
      state.filterChangesCounter += 1;
    },
    departureMonthSelected: (state, action) => {
      toggleSelected(state.filterGroups.departureMonth.selected, action.payload);
      state.filterChangesCounter += 1;
    },
    // Ship ratings are **numbers**, where every other group holds strings — the query parse maps
    // them through `parseInt` and the component dispatches a number.
    shipRatingSelected: (state, action) => {
      toggleSelected(state.filterGroups.shipRating.selected, action.payload);
      state.filterChangesCounter += 1;
    },
    durationSelected: (state, action) => {
      state.filterGroups.duration.selectedMinValue = action.payload.lowValue;
      state.filterGroups.duration.selectedMaxValue = action.payload.highValue;
      state.filterChangesCounter += 1;
    },
    cruiseCategorySelected: (state, action) => {
      toggleSelected(state.filterGroups.cruiseCategory.selected, action.payload);
      state.filterChangesCounter += 1;
    },
    destinationSelected: (state, action) => {
      toggleSelected(state.filterGroups.destination.selected, action.payload);
      state.filterChangesCounter += 1;
    },
    numberOfDestinationsSelected: (state, action) => {
      state.filterGroups.destination.selectedMinNumberOfDestinations = action.payload.lowValue;
      state.filterGroups.destination.selectedMaxNumberOfDestinations = action.payload.highValue;
      state.filterChangesCounter += 1;
    },
    shippingCompanySelected: (state, action) => {
      toggleSelected(state.filterGroups.shippingCompany.selected, action.payload);
      state.filterChangesCounter += 1;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(initSucceeded, (state, action) => {
      const { filter, labels, preSelectedFilter } = action.payload;
      const groups = state.filterGroups;

      // Reads the current URL from inside the reducer, as before, and takes the site's
      // pre-selected filter as the fallback query string. Preserved rather than lifted into the
      // thunk: that would change *when* the query is read.
      const query = urlUtils.getFilterStateFromQueryByFiltersType(queryKeys.CRUISE_FILTERS, preSelectedFilter);

      const departureMonthInQuery = parseList(query[queryKeys.CRUISE_FILTERS.departureMonth]);
      const shipRatingInQuery = query[queryKeys.CRUISE_FILTERS.shipRating]
        ? query[queryKeys.CRUISE_FILTERS.shipRating].split(',').map((r) => parseInt(r, 10))
        : null;
      const durationInQuery = parseList(query[queryKeys.CRUISE_FILTERS.duration]);
      const cruiseCategoryInQuery = parseList(query[queryKeys.CRUISE_FILTERS.cruiseCategory]);
      const destinationInQuery = parseList(query[queryKeys.CRUISE_FILTERS.destination]);
      const nOfDestInQuery = parseList(query[queryKeys.CRUISE_FILTERS.numberOfDestinations]);
      const shippingCompanyInQuery = parseList(query[queryKeys.CRUISE_FILTERS.shippingCompany]);

      // Add filter options to state.
      if (filter) {
        groups.departureMonth.options = filter.departureMonths;
        groups.shipRating.options = filter.shipRatingOptions;
        groups.duration.minValue = filter.minDuration;
        groups.duration.maxValue = filter.maxDuration;
        groups.cruiseCategory.options = filter.cruiseCategoryOptions.map(({ id, name }) => ({ id, name }));
        groups.destination.options = filter.destinationOptions.map(({ id, name }) => ({ id, name }));
        groups.destination.minNumberOfDestinations = filter.minNumberOfDestinations;
        groups.destination.maxNumberOfDestinations = filter.maxNumberOfDestinations;
        groups.shippingCompany.options = filter.shippingCompanyOptions.map(({ id, name }) => ({ id, name }));
      }

      // Set filter values from query
      if (departureMonthInQuery) {
        groups.departureMonth.selected = departureMonthInQuery;
      }
      if (shipRatingInQuery) {
        groups.shipRating.selected = shipRatingInQuery;
      }
      if (durationInQuery) {
        groups.duration.selectedMinValue = parseInt(durationInQuery[0], 10);
        groups.duration.selectedMaxValue = parseInt(durationInQuery[1], 10);
      }
      if (cruiseCategoryInQuery) {
        groups.cruiseCategory.selected = cruiseCategoryInQuery;
      }
      if (destinationInQuery) {
        groups.destination.selected = destinationInQuery;
      }
      if (nOfDestInQuery) {
        groups.destination.selectedMinNumberOfDestinations = parseInt(nOfDestInQuery[0], 10);
        groups.destination.selectedMaxNumberOfDestinations = parseInt(nOfDestInQuery[1], 10);
      }
      if (shippingCompanyInQuery) {
        groups.shippingCompany.selected = shippingCompanyInQuery;
      }

      // Add filter labels to state.
      if (labels) {
        groups.departureMonth.heading = labels.filter_departureMonth_heading;
        groups.shipRating.heading = labels.filter_shipRating_heading;
        groups.duration.heading = labels.filter_duration_heading;
        groups.duration.toLabel = labels.filter_duration_toLabel;
        groups.duration.dayLabel = labels.filter_duration_dayLabel;
        groups.duration.daysLabel = labels.filter_duration_daysLabel;
        groups.cruiseCategory.heading = labels.filter_cruiseCategory_heading;
        groups.destination.heading = labels.filter_destination_heading;
        groups.destination.numberOfDestinationsHeading = labels.filter_destination_numberOfDestinationsHeading;
        groups.destination.toLabel = labels.filter_destination_toLabel;
        groups.destination.destinationsLabel = labels.filter_destination_destinationsLabel;
        groups.shippingCompany.heading = labels.filter_shippingCompany_heading;
      }
    });
  },
});

export const {
  cruiseCategorySelected,
  departureMonthSelected,
  destinationSelected,
  durationSelected,
  filterPanelToggled,
  filtersCleared,
  numberOfDestinationsSelected,
  shipRatingSelected,
  shippingCompanySelected,
  singleFilterCleared,
} = filterSlice.actions;

export default filterSlice.reducer;
